<template>
  <div class="px-4 pt-3">
    <v-card outlined class="mb-3">
      <v-card-title class="pa-3">
        <div class="text-h6">선택지 그룹 관리</div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="categoryFilter"
          label="카테고리 필터"
          placeholder="price / win_lose / scoreline / over_under / quiz"
          outlined dense hide-details class="mr-2" style="max-width:300px"
          @keyup.enter="loadGroups"
          clearable @click:clear="categoryFilter=''; loadGroups()"
        ></v-text-field>
        <v-btn small color="primary" depressed @click="openCreateDialog">
          <v-icon left small>mdi-plus</v-icon>그룹 추가
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="groups"
        :items-per-page="20"
        :loading="loading"
        class="elevation-0"
        dense
      >
        <template v-slot:item.category="{ item }">
          <v-chip x-small outlined>{{ item.category }}</v-chip>
        </template>
        <template v-slot:item.is_active="{ item }">
          <v-chip x-small :color="item.is_active ? 'green' : 'grey'" dark>
            {{ item.is_active ? 'active' : 'inactive' }}
          </v-chip>
        </template>
        <template v-slot:item.choices="{ item }">
          <div class="d-flex flex-wrap" style="gap:4px">
            <v-chip v-for="c in item.choices" :key="c.choice_id" x-small label outlined>
              <strong class="mr-1">{{ c.code }}</strong>
              <span class="grey--text">{{ c.label }}</span>
            </v-chip>
          </div>
        </template>
        <template v-slot:item.created_at="{ item }">
          <span class="caption">{{ formatDate(item.created_at) }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- 그룹 등록 다이얼로그 -->
    <v-dialog v-model="dialog" max-width="780" scrollable>
      <v-card>
        <v-card-title>
          <span class="text-h6">선택지 그룹 신규 등록</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="form.group_code"
            label="그룹 코드 (영문/숫자/_, unique) *"
            outlined dense class="mb-3"
            placeholder="football_score_basic"
            persistent-hint hint="예: football_score_basic / quiz_4ch_basic. 등록 후 변경 불가."
          ></v-text-field>
          <v-text-field
            v-model="form.group_name"
            label="그룹 표시명 *"
            outlined dense hide-details class="mb-3"
            placeholder="축구 점수형 기본"
          ></v-text-field>
          <v-select
            v-model="form.category"
            :items="categoryOptions"
            label="카테고리 *"
            outlined dense hide-details class="mb-2"
          ></v-select>
          <v-alert v-if="selectedCategorySample" dense text type="info" class="mb-3 body-2">
            <strong>코드 규칙:</strong> {{ selectedCategorySample }}
          </v-alert>
          <v-text-field
            v-model="form.description"
            label="설명 (선택)"
            outlined dense hide-details class="mb-3"
          ></v-text-field>
          <v-divider class="my-3"></v-divider>
          <div class="subtitle-2 mb-2">선택지 (최소 2개) *</div>
          <v-data-table
            :headers="choiceHeaders"
            :items="form.choices"
            hide-default-footer
            :items-per-page="-1"
            dense class="mb-2"
          >
            <template v-slot:item.code="{ item, index }">
              <v-text-field v-model="item.code" dense hide-details placeholder="up / s_2_1"></v-text-field>
            </template>
            <template v-slot:item.label="{ item }">
              <v-text-field v-model="item.label" dense hide-details placeholder="상승 / 2:1"></v-text-field>
            </template>
            <template v-slot:item.actions="{ index }">
              <v-btn icon small @click="removeChoice(index)"><v-icon small>mdi-delete</v-icon></v-btn>
            </template>
          </v-data-table>
          <v-btn small outlined @click="addChoice">
            <v-icon left small>mdi-plus</v-icon>선택지 추가
          </v-btn>

          <v-alert v-if="dialogError" dense text type="error" class="mt-3">{{ dialogError }}</v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">취소</v-btn>
          <v-btn color="primary" depressed :loading="saving" @click="saveGroup">등록</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'Danta_ChoiceGroupManage',
  data: () => ({
    loading: false,
    groups: [],
    categoryFilter: '',
    headers: [
      { text: 'ID', value: 'group_id', width: '60px' },
      { text: '코드', value: 'group_code', width: '180px' },
      { text: '표시명', value: 'group_name', width: '160px' },
      { text: '카테고리', value: 'category', width: '110px' },
      { text: '상태', value: 'is_active', width: '90px' },
      { text: '선택지', value: 'choices' },
      { text: '등록일', value: 'created_at', width: '140px' },
    ],
    choiceHeaders: [
      { text: '코드', value: 'code', width: '180px', sortable: false },
      { text: '라벨', value: 'label', width: '260px', sortable: false },
      { text: '', value: 'actions', width: '50px', sortable: false },
    ],
    // 카테고리 옵션 — 코드 규칙 명시 (선택지 등록 시 일관성 유지용).
    //   sample: 권장 코드 형식 예시 — 등록 폼 안내 alert 에 표시.
    categoryOptions: [
      {
        text: '가격형 (price) — 코드: up / down',
        value: 'price',
        sample: 'up / down (가격 방향 2지선다, 예: BTC/ETH)',
      },
      {
        text: '승무패 (win_lose) — 코드: win / lose (또는 home / draw / away)',
        value: 'win_lose',
        sample: 'win / lose (또는 home / draw / away — 무승부 있는 종목)',
      },
      {
        text: '점수형 (scoreline) — 코드: s_홈_원정 (예: s_2_1, s_1_1, s_0_2, s_other)',
        value: 'scoreline',
        sample: 's_2_1 / s_1_1 / s_0_2 / s_other (홈팀 점수 _ 원정팀 점수)',
      },
      {
        text: '오버언더 (over_under) — 코드: over / under',
        value: 'over_under',
        sample: 'over / under (기준값 이상/이하 2지선다)',
      },
      {
        text: '퀴즈 (quiz) — 코드: a / b / c / d (자유 N지선다)',
        value: 'quiz',
        sample: 'a / b / c / d (선택지 알파벳 순. 의미는 label 에 명시)',
      },
    ],
    dialog: false,
    saving: false,
    dialogError: '',
    form: {
      group_code: '', group_name: '', category: 'win_lose',
      description: '',
      choices: [
        { code: '', label: '' },
        { code: '', label: '' },
      ],
    },
  }),
  computed: {
    // 현재 선택된 카테고리의 코드 규칙 sample (등록 폼 안내용)
    selectedCategorySample() {
      const opt = this.categoryOptions.find(o => o.value === this.form.category);
      return opt?.sample || '';
    },
  },
  created() { this.loadGroups(); },
  methods: {
    ...mapActions(['DANTA_GET_CHOICE_GROUPS', 'DANTA_CREATE_CHOICE_GROUP']),
    async loadGroups() {
      this.loading = true;
      try {
        const r = await this.DANTA_GET_CHOICE_GROUPS({
          category: this.categoryFilter || undefined,
        });
        this.groups = r?.groups || [];
      } catch (e) {
        console.error('choice_groups 조회 실패:', e);
      } finally {
        this.loading = false;
      }
    },
    addChoice() {
      this.form.choices.push({ code: '', label: '' });
    },
    removeChoice(i) {
      if (this.form.choices.length <= 2) return;
      this.form.choices.splice(i, 1);
    },
    openCreateDialog() {
      this.form = {
        group_code: '', group_name: '', category: 'win_lose',
        description: '',
        choices: [
          { code: '', label: '' },
          { code: '', label: '' },
        ],
      };
      this.dialogError = '';
      this.dialog = true;
    },
    validate() {
      if (!this.form.group_code) return '그룹 코드 필수';
      if (!this.form.group_name) return '그룹 표시명 필수';
      if (!this.form.category)   return '카테고리 필수';
      const codes = this.form.choices.map(c => (c.code || '').trim()).filter(Boolean);
      if (codes.length < 2) return '선택지 최소 2개 (코드 필수)';
      const uniq = new Set(codes);
      if (uniq.size !== codes.length) return '선택지 코드 중복';
      return null;
    },
    async saveGroup() {
      const err = this.validate();
      if (err) { this.dialogError = err; return; }
      this.dialogError = '';
      this.saving = true;
      try {
        await this.DANTA_CREATE_CHOICE_GROUP({
          group_code: this.form.group_code.trim(),
          group_name: this.form.group_name.trim(),
          category: this.form.category,
          description: this.form.description || '',
          choices: this.form.choices.filter(c => c.code).map((c, i) => ({
            code: c.code.trim(),
            label: c.label || c.code,
            sort_order: i + 1,
            meta: {},
          })),
        });
        this.dialog = false;
        await this.loadGroups();
      } catch (e) {
        const msg = e?.response?.data?.err_message || e?.message || '등록 실패';
        this.dialogError = msg;
      } finally {
        this.saving = false;
      }
    },
    formatDate(iso) {
      if (!iso) return '';
      try { return new Date(iso).toLocaleString('ko-KR'); } catch (_) { return iso; }
    },
  },
};
</script>
